/**
 *  @class
 *  @function FacetsToggle
 */
class FacetsToggle {
  constructor() {
    this.container = document.getElementById('Facet-Drawer');
    let button = document.getElementById('Facets-Toggle');

    // Add functionality to buttons
    if (button) {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        document.getElementsByTagName('body')[0].classList.toggle('open-cc');
        if (this.container) this.container.classList.toggle('active');
      });
    }

    setTimeout(function () {
      window.dispatchEvent(new Event('resize.resize-select'));
    }, 10);
  }
}

/* ------------------------------------------------------------
  ADA: Ensure price range inputs have accessible labels
  - Your filters re-render via fetch() + DOM replacement.
  - This re-applies labels after every render.
------------------------------------------------------------ */
function ensurePriceRangeLabels(scope = document) {
  const minInput = scope.querySelector('input[name="filter.v.price.gte"]');
  const maxInput = scope.querySelector('input[name="filter.v.price.lte"]');

  function addLabel(input, text, fallbackId) {
    if (!input) return;

    // Ensure the input has an id so label[for] works (WAVE requirement)
    if (!input.id) input.id = fallbackId;

    // If label already exists, stop
    if (document.querySelector(`label[for="${CSS.escape(input.id)}"]`)) {
      // Still ensure aria-label exists for extra robustness
      if (!input.getAttribute('aria-label')) input.setAttribute('aria-label', text);
      return;
    }

    const label = document.createElement('label');
    label.className = 'visually-hidden';
    label.setAttribute('for', input.id);
    label.textContent = text;

    input.insertAdjacentElement('beforebegin', label);

    // Extra safety for SR + axe
    if (!input.getAttribute('aria-label')) input.setAttribute('aria-label', text);
  }

  addLabel(minInput, 'Minimum price', 'Filter-Price-GTE');
  addLabel(maxInput, 'Maximum price', 'Filter-Price-LTE');
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('input', this.debouncedOnSubmit.bind(this));
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const container = document.getElementsByClassName('thb-filter-count');
    const gridContainer = document.getElementById('ProductGridContainer');

    if (gridContainer) {
      const collectionEl = gridContainer.querySelector('.collection');
      if (collectionEl) collectionEl.classList.add('loading');
    }

    for (var item of container) {
      item.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event);
      }
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          {
            html,
            url,
          },
        ];

        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);

        new FacetsToggle();

        // ADA: ensure labels exist after async fetch render
        ensurePriceRangeLabels(document);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;

    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);

    new FacetsToggle();

    // ADA: ensure labels exist after cache render
    ensurePriceRangeLabels(document);
  }

  static renderProductGridContainer(html) {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const newContainer = parsed.getElementById('ProductGridContainer');
    if (!newContainer) return;

    container.innerHTML = newContainer.innerHTML;

    let top =
      container.getBoundingClientRect().top +
      document.documentElement.scrollTop -
      parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) -
      30;

    window.scrollTo({
      top: top,
      left: 0,
      behavior: 'smooth',
    });

    // ADA: grid render can affect filter UI; re-ensure labels
    ensurePriceRangeLabels(document);
  }

  static renderProductCount(html) {
    const countHtml = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
    const container = document.getElementsByClassName('thb-filter-count');

    if (countHtml) {
      for (var item of container) {
        item.innerHTML = countHtml.innerHTML;
        item.classList.remove('loading');
      }
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm collapsible-row, #FacetFiltersFormMobile collapsible-row'
    );

    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('collapsible-row') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };

    const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const target = document.querySelector(`collapsible-row[data-index="${element.dataset.index}"]`);
      if (target) target.innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    new FacetsToggle();

    // ADA: after filters re-render, add missing price labels
    ensurePriceRangeLabels(document);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('collapsible-row'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const target = document.querySelector(selector);
      if (target) target.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-filter-count'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      const target = document.querySelector(selector);
      if (target) target.innerHTML = html.querySelector(selector).innerHTML;
    });
  }

  static renderCounts(source, target) {
    if (!target) return;

    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  onSubmitHandler(event) {
    event.preventDefault();

    const formEl = event.target.closest('form');
    if (!formEl) return;

    const formData = new FormData(formEl);
    const searchParams = new URLSearchParams(formData);

    if (searchParams.get('filter.v.price.gte') === '0.00') {
      searchParams.delete('filter.v.price.gte');
    }

    if (document.querySelector('.price_slider')) {
      if (
        searchParams.get('filter.v.price.lte') ===
        parseFloat(document.querySelector('.price_slider').dataset.max).toFixed(2)
      ) {
        searchParams.delete('filter.v.price.lte');
      }
    }

    FacetFiltersForm.renderPage(searchParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();

    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);

    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);

customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('a').forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();
        const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
        if (form) form.onActiveFilterClick(event);
      });
    });
  }
}
customElements.define('facet-remove', FacetRemove);

/**
 *  @class
 *  @function PriceSlider
 */
class PriceSlider extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    let slider = this.querySelector('.price_slider'),
      amounts = this.querySelector('.price_slider_amount'),
      args = {
        start: [parseFloat(slider.dataset.minValue || 0), parseFloat(slider.dataset.maxValue || slider.dataset.max)],
        connect: true,
        step: 10,
        direction: document.dir,
        handleAttributes: [{ 'aria-label': 'lower' }, { 'aria-label': 'upper' }],
        range: {
          min: 0,
          max: parseFloat(slider.dataset.max),
        },
      },
      event = new CustomEvent('input'),
      form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');

    if (!slider || !amounts) return;

    if (slider.classList.contains('noUi-target')) {
      slider.noUiSlider.destroy();
    }

    noUiSlider.create(slider, args);

    slider.noUiSlider.on('update', function (values) {
      const minField = amounts.querySelector('.field__input_min');
      const maxField = amounts.querySelector('.field__input_max');
      if (minField) minField.value = values[0];
      if (maxField) maxField.value = values[1];

      // ADA: ensure labels remain even if slider rebuilds DOM
      ensurePriceRangeLabels(document);
    });

    slider.noUiSlider.on('change', function () {
      if (form) form.querySelector('form').dispatchEvent(event);
    });
  }
}
customElements.define('price-slider', PriceSlider);

window.addEventListener('load', () => {
  new FacetsToggle();
  // ADA: initial pass
  ensurePriceRangeLabels(document);
});